import React, { useState, useMemo, useCallback, useRef, useEffect, useLayoutEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Check, X, Info, Landmark } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { cn } from "@/lib/utils";
import { pluralize } from "@/utils/pluralize";
import {
  AnalysisOutput,
  DenyReason,
  AssessmentPosition,
  FunderGroup,
  FunderWithdrawal,
  PullFrequency,
  RESOLVABLE_CADENCES,
  ResolvableCadence,
} from "@/types/underwriting";
import { useAgentPageState } from "@/agent/pageState/registry";
import { analysisPageSlot, offerPageSlot } from "./agentPageSlots";
import FunderCard from "./FunderCard";
import { DecisionColumn, type DecisionOutcome } from "./DecisionColumn";
import { DecisionStrip } from "./DecisionStrip";
import { HEADER_LABEL, HEADER_PANE } from "./headerStyles";
import { RevenueMonthsDialog } from "./RevenueMonthsDialog";
import { withdrawalInLeverage, withdrawalMonthlyPayout } from "./funderLeverage";
import { sortByLastPaymentDesc } from "./funderOrdering";
import { pruneMatchingLeverageOverrides, setOverride, withdrawalKey } from "./withdrawalOverrides";
import DailyBalance, { DAILY_BALANCES_SECTION_ID } from "@/components/application/DailyBalance";
import OfferCalculator from "./OfferCalculator";
import { countPossibleMissedFromOffer } from "@/utils/balanceLowThreshold";
import {
  averageForMonthKeys,
  avgMonthsFingerprint,
  buildAvgRevenueMonths,
  formatAvgRevenueDisplay,
  monthKeyToAbbrev,
  reconcileAvgMonthSelection,
} from "@/utils/avgRevenueMonths";
import {
  OfferValues,
  ActivePosition,
  AcceptDialog,
  DenyDialog,
  ChangeDecisionDialog,
} from "./UnderwritingAssessmentDialogs";
import { useUnderwritingAssessment } from "@/hooks/useUnderwritingAssessment";
import { useBorrowerDetail } from "@/hooks/useBorrowerDetail";
import { useOrganizationSettingsQuery } from "@/hooks/queries";
import { resolveUnderwritingNumber, underwritingRuleValue } from "@/services/organizationSettings";
import { resolveBusinessDaysPerMonth } from "@/utils/offerCalculations";
import { POST_FUNDING_APP_STATUSES } from "@/types/borrower";
import { handleApiError } from "@/services/api";
import { AxiosError, isAxiosError } from "axios";
import {
  applyInvalidations,
  INVALIDATES,
} from "@/components/application/merchant-tasks/api/invalidates";
import { merchantFlowService } from "@/components/application/merchant-tasks/api/merchantFlowService";
import ContractPreviewModal from "@/components/application/merchant-tasks/send/ContractPreviewModal";
import { useContractPreview } from "@/components/application/merchant-tasks/send/useContractPreview";
import { classifySendFailure } from "@/components/application/merchant-tasks/send/useSendFlow";
import { planDefaultOnboarding } from "@/components/application/merchant-tasks/send/defaultOnboarding";
import { hasMainContract } from "@/components/application/merchant-tasks/send/hasMainContract";
import { approvalOnboardingDecision } from "./approvalOnboarding";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { format, parseISO } from "date-fns";
import BaseTransactionTable, { TableColumn } from "@/components/transactions/BaseTransactionTable";
import { isSameFunder } from "./funderName";
import { BorrowerTransaction, useTransactions } from "@/hooks/useTransactions";
import ExpandableRow, {
  CellDef,
  DetailDef,
  formatCurrency,
} from "@/components/transactions/ExpandableRow";
import TransactionFiltersComponent, {
  TransactionFilters,
  defaultFilters,
} from "@/components/transactions/TransactionFilters";
import {
  CategoryOverrideSelect,
  RevenueOverrideSelect,
  categoryDisplayLabel,
  revenueLabel,
  saveTransactionClassification,
} from "@/components/transactions/ClassificationOverrideSelects";
import type { AnalysisOverridePayload } from "@/types/analysisRun";
import {
  monthCreditsFilters,
  type AnalysisListLink,
  type AnalysisListLinkState,
} from "@/components/application/aiAnalysis/analysisListLink";

interface FunderAnalysisDashboardProps {
  analysis: AnalysisOutput;
  borrowerId?: number;
  readOnly?: boolean;
  /** Which enriched-transaction lane to load (matches underwriting data source). */
  transactionSource?: "statements" | "plaid";
  /**
   * When provided, use these transactions instead of fetching the enriched
   * lane (used by the experimental AI Analysis tab, whose transactions live
   * on the AnalysisRun rather than in EnrichedTransaction).
   */
  transactionsOverride?: BorrowerTransaction[];
  /** Extra header column: the Plaid asset PDF preview on Final Underwriting. Default: none. */
  topRightAction?: "preview-plaid-pdf";
  onPreviewPlaidPdf?: () => void;
  /**
   * Show the Revenue/Counterparty transaction-table columns. These are only
   * populated by the experimental AI Analysis (EXP) run's enriched transactions
   * (rev/counterparty tags from stage 1) — MoneyThumb's enriched-transaction
   * lane doesn't supply them, so other consumers of this dashboard (Final
   * Underwriting, standard AI Analysis tab, broker view) leave this off.
   * Default: false.
   */
  showEnrichedColumns?: boolean;
  /**
   * What the Plaid PDF slot shows when there is no asset report to preview:
   * "manual-login" for a deal forced past a stuck/failed Plaid flow,
   * "no-report" for one that reached final underwriting without Plaid at all
   * (e.g. requires_plaid is false).
   */
  pdfSlotMode?: "button" | "manual-login" | "no-report";
  /**
   * When provided, toggling a position's active dot is saved as a correction
   * rather than kept as local what-if state. The EXP tab supplies this so the
   * header, the offer and the decision panel all move together; without it the
   * toggle stays client-side, which is all the MoneyThumb dashboard can do.
   *
   * Must reject if the save failed, so the dot can be put back: the click is
   * applied locally first to keep the UI responsive, and a rejected save that
   * left the flip in place had the dot claiming a correction that was never
   * stored.
   */
  onPersistActiveToggle?: (overrideKey: string, includeInLeverage: boolean) => Promise<unknown>;
  /**
   * When provided, frequency dropdown changes are saved as position corrections
   * (AnalysisOverride field ``frequency``), except ``unknown`` which callers
   * should treat as clearing that field so a re-run can re-detect cadence.
   * Same optimistic/restore pattern as ``onPersistActiveToggle``.
   */
  onPersistFrequency?: (overrideKey: string, frequency: ResolvableCadence) => Promise<unknown>;
  /**
   * When provided (and the row has ``override_key``), Category and Revenue
   * cells become editable AnalysisOverride saves — same path as All Transactions.
   * The AI Analysis tab supplies this; legacy MoneyThumb dashboards leave it off.
   */
  onSaveTransactionOverride?: (payload: AnalysisOverridePayload) => void;
  /**
   * @deprecated Prefer ``onPersistFrequency``. Kept so older call sites that
   * only enabled local unknown-cadence resolution still type-check during
   * migration; ignored when ``onPersistFrequency`` is set.
   */
  enableCadenceResolution?: boolean;
  /**
   * AI Analysis approve: after the assessment is saved, email the merchant
   * the default onboarding flow when the org still has automatic sending on.
   * The confirm dialog says so before they commit.
   */
  startDefaultOnboarding?: boolean;
  /**
   * AI Analysis: the transaction list's state from the address ("Show in application"): a
   * filter, and rows to highlight, the first scrolled into view. Changing the list by hand
   * ends the link. Absent, or carrying no link, the list is as it is without one.
   */
  listLink?: AnalysisListLinkState;
}

type FunderFilter =
  | { type: "funder"; name: string }
  | { type: "withdrawal"; name: string; amount: number }
  | { type: "deposit"; name: string; amount: number; date: string };

const deriveAllMonthKeys = (analysis: AnalysisOutput): string[] => {
  const keys = new Set<string>();

  if (analysis.calendar_transactions) {
    for (const tx of analysis.calendar_transactions) {
      if (tx.date) keys.add(tx.date.slice(0, 7));
    }
  }

  if (analysis.statement_start_date) {
    keys.add(analysis.statement_start_date.slice(0, 7));
  }
  if (analysis.statement_end_date) {
    keys.add(analysis.statement_end_date.slice(0, 7));
  }

  for (const rl of analysis.revenue_lines || []) {
    const match = rl.month_abbrev;
    if (match) {
      for (const tx of analysis.calendar_transactions || []) {
        const txMonth = parseISO(tx.date);
        const txAbbrev = format(txMonth, "MMM");
        if (txAbbrev === match) {
          keys.add(tx.date.slice(0, 7));
        }
      }
    }
  }

  return [...keys].sort();
};

const NO_MONTHS: string[] = [];

/** Base page size the transactions pane is sized to show without an inner scrollbar. */
const TXN_TABLE_VIEWPORT_ROWS = 20;

/**
 * Toggle and cadence state is keyed by this, not by funder name: a merchant can
 * hold an advance and a credit line with the same lender, and keying on the name
 * alone made toggling one silently toggle the other.
 */
const funderKey = (fg: Pick<FunderGroup, "name" | "position_type">): string =>
  `${fg.position_type ?? "mca"}|${fg.name}`;

type PositionSection = "mca" | "loc_loan";

type TopRightAction = NonNullable<FunderAnalysisDashboardProps["topRightAction"]>;

/** Narrow boards, both variants: one card per row, the decisions last, under the calculator. */
const STACKED_AREAS = "[grid-template-areas:'rev'_'lev'_'calc'_'panes']";

/**
 * Header arrangement per variant: Revenue, Leverage + Payout, the calculator and the
 * decision panes, placed by area. The header keeps one row as long as it fits: the
 * calculator grows first, up to 24.25rem (3 columns where even extreme amounts fit),
 * and Revenue takes what is left (always months beside Avg, its contents shrinking);
 * as the row tightens Leverage + Payout and the panes tighten their padding, the
 * panes go down to their content and the calculator drops to 2 columns. Past that it breaks into [Revenue][Leverage] over
 * [calculator][decisions]; narrower still both variants stack one card per row, with the
 * decisions last. Where the calculator and the decisions meet they join into one card:
 * side by side in two rows, the decisions under the calculator when stacked.
 *
 * Each switch is a container query on the board's own width, which the sidebar and
 * the page's max width change independently of the viewport. Leverage and the panes
 * get content-sized tracks: their cards clip overflow, so auto tracks could squeeze
 * them below their content. Measured minimums: Revenue 20rem (below that its month
 * chips would clip, even at the smallest type), Leverage + Payout 207px at its tighter
 * padding, the calculator 12rem in 2 columns / 18.25rem in 3 (larger amounts clip at
 * those floors), the two Final Underwriting panes 269px
 * (one-line labels; a decided Final Decision is the wider one), the AI Decision pane
 * 140px.
 */
const HEADER_LAYOUT: Record<
  TopRightAction | "none",
  { header: string; calc: string; panes: string; pane: string }
> = {
  // Final Underwriting: Plaid Asset Report, Final Decision. One row from 63.25rem; from
  // 37.75rem two rows; narrower, stacked.
  "preview-plaid-pdf": {
    header: [
      STACKED_AREAS,
      "[@container_board_(min-width:37.75rem)_and_(max-width:63.24rem)]:grid-cols-[minmax(0,1fr)_max-content]",
      "[@container_board_(min-width:37.75rem)_and_(max-width:63.24rem)]:[grid-template-areas:'rev_lev'_'calc_panes']",
      "[@container_board_(min-width:63.25rem)]:grid-cols-[minmax(20rem,1fr)_max-content_minmax(12rem,24.25rem)_minmax(min-content,max-content)]",
      "[@container_board_(min-width:63.25rem)]:[grid-template-areas:'rev_lev_calc_panes']",
    ].join(" "),
    // Joined to the panes: over them when stacked, beside them from 37.75rem.
    calc: "[@container_board_(max-width:37.74rem)]:rounded-b-none [@container_board_(min-width:37.75rem)]:rounded-r-none",
    // The negative margin closes the grid gap so the panes share the calculator's border.
    panes: [
      "grid-cols-2 [@container_board_(min-width:37.75rem)]:grid-cols-[repeat(2,minmax(min-content,7.75rem))] [@container_board_(min-width:37.75rem)]:[&>*]:px-2",
      "[@container_board_(max-width:37.74rem)]:-mt-3 [@container_board_(max-width:37.74rem)]:rounded-t-none [@container_board_(max-width:37.74rem)]:border-t-0",
      "[@container_board_(min-width:37.75rem)]:-ml-3 [@container_board_(min-width:37.75rem)]:rounded-l-none [@container_board_(min-width:37.75rem)]:border-l-0",
    ].join(" "),
    pane: "border-l",
  },
  // AI Analysis: Decision only. One row from 55.25rem; from 35.75rem two rows; narrower,
  // stacked.
  none: {
    header: [
      STACKED_AREAS,
      "[@container_board_(min-width:35.75rem)_and_(max-width:55.24rem)]:grid-cols-[minmax(0,1fr)_max-content]",
      "[@container_board_(min-width:35.75rem)_and_(max-width:55.24rem)]:[grid-template-areas:'rev_lev'_'calc_panes']",
      "[@container_board_(min-width:55.25rem)]:grid-cols-[minmax(20rem,1fr)_max-content_minmax(12rem,24.25rem)_minmax(min-content,10rem)]",
      "[@container_board_(min-width:55.25rem)]:[grid-template-areas:'rev_lev_calc_panes']",
    ].join(" "),
    calc: "[@container_board_(max-width:35.74rem)]:rounded-b-none [@container_board_(min-width:35.75rem)]:rounded-r-none",
    panes: [
      "[@container_board_(max-width:35.74rem)]:-mt-3 [@container_board_(max-width:35.74rem)]:rounded-t-none [@container_board_(max-width:35.74rem)]:border-t-0",
      "[@container_board_(min-width:35.75rem)]:[&>*]:px-2 [@container_board_(min-width:35.75rem)]:-ml-3 [@container_board_(min-width:35.75rem)]:rounded-l-none [@container_board_(min-width:35.75rem)]:border-l-0",
    ].join(" "),
    pane: "",
  },
};

const FunderAnalysisDashboard: React.FC<FunderAnalysisDashboardProps> = ({
  analysis,
  borrowerId,
  readOnly = false,
  transactionSource = "statements",
  transactionsOverride,
  topRightAction,
  onPreviewPlaidPdf,
  showEnrichedColumns = false,
  pdfSlotMode = "button",
  onPersistActiveToggle,
  onPersistFrequency,
  onSaveTransactionOverride,
  enableCadenceResolution = false,
  startDefaultOnboarding = false,
  listLink,
}) => {
  const queryClient = useQueryClient();
  const {
    isFunderStaff,
    isUnderwritingActor,
    canFundOrDeclineFromFinal,
    canSendMerchantTasks: canSendMerchantTasksFor,
  } = useCurrentUser();
  const { borrower, patch } = useBorrowerDetail(borrowerId ?? null);

  const { existingAssessment, submit, update } = useUnderwritingAssessment(borrowerId);
  const { data: organizationSettings, isError: orgSettingsError } = useOrganizationSettingsQuery();
  // Null until settings load. A failed read keeps today's default (send).
  const autoSendContractsEnabled = organizationSettings
    ? organizationSettings.auto_send_contracts_enabled !== false
    : orgSettingsError
      ? true
      : null;
  const actorCanSendMerchantTasks = canSendMerchantTasksFor(organizationSettings);
  const canActOnAssessment = !readOnly && isUnderwritingActor;
  const canActOnFinalDecision = canFundOrDeclineFromFinal;
  const businessDaysPerMonth = resolveBusinessDaysPerMonth(
    underwritingRuleValue(organizationSettings, "business_days_per_month")
  );
  const leverageWarnPct = resolveUnderwritingNumber(
    underwritingRuleValue(organizationSettings, "target_leverage_percent"),
    30
  );
  const { transactions: fetchedTransactions } = useTransactions(
    transactionsOverride ? null : (borrowerId ?? null),
    transactionSource
  );
  const transactions = transactionsOverride ?? fetchedTransactions;
  const nsfDates = useMemo(
    () =>
      transactions
        .filter((tx) => tx.is_nsf === true || tx.category === "NSF_RETURN")
        .map((tx) => tx.date.slice(0, 10)),
    [transactions]
  );

  const analysisMonthKeys = useMemo(() => deriveAllMonthKeys(analysis), [analysis]);

  const effectiveMonthKeys = useMemo(() => {
    const keys = new Set<string>(analysisMonthKeys);
    for (const tx of transactions) {
      const d = tx.date;
      if (d && typeof d === "string" && d.length >= 7) {
        keys.add(d.slice(0, 7));
      }
    }
    const sorted = [...keys].sort();
    if (sorted.length === 0) {
      return [format(new Date(), "yyyy-MM")];
    }
    return sorted;
  }, [analysisMonthKeys, transactions]);

  const [listFilters, setListFilters] = useState<TransactionFilters>(defaultFilters);

  const [calendarFilters, setCalendarFilters] = useState<{ funders: string[]; txTypes: string[] }>({
    funders: [],
    txTypes: [],
  });

  const [expandedRowId, setExpandedRowId] = useState<number | string | null>(null);
  /**
   * Per-withdrawal persist generation and in-flight set. A newer click bumps the
   * generation so a stale failure cannot restore over a click that already won;
   * in-flight blocks a second click on the same position until the save settles.
   */
  const toggleRequestGen = useRef(new Map<string, number>());

  // funderKey -> { withdrawalKey -> boolean }
  const [withdrawalOverrides, setWithdrawalOverrides] = useState<
    Record<string, Record<string, boolean>>
  >({});
  /**
   * Cadences a reviewer assigned by hand to positions detection could not put on
   * a schedule. Client-only and intentionally unsaved: the EXP tab is in beta
   * and these are review what-ifs, not corrections to a borrower's record.
   */
  const [cadenceOverrides, setCadenceOverrides] = useState<
    Record<string, Record<string, ResolvableCadence>>
  >({});
  const [selectedFilter, setSelectedFilter] = useState<FunderFilter | null>(null);
  /** Only these rows, by override key (a link's proposal or position rows). */
  const [keyFilter, setKeyFilter] = useState<string[] | null>(null);
  // ── Link state ("Show in application") ──
  /** Whether the list shows a link's view, untouched since. */
  const linkApplied = useRef(false);
  const latestListLink = useRef(listLink);
  useEffect(() => {
    latestListLink.current = listLink;
  });
  /**
   * Called by every filter change made by hand: it ends the link. Its params leave the address
   * (the list keeps the change), so a reload or a bookmark does not claim a view that is gone.
   */
  // Bumped when a link ends: the table goes back to its first page (the link may have opened another).
  const [linkEnds, setLinkEnds] = useState(0);
  const endLink = useCallback(() => {
    if (!linkApplied.current) return;
    linkApplied.current = false;
    setLinkEnds((count) => count + 1);
    latestListLink.current?.leave();
  }, []);
  const changeListFilters = useCallback(
    (next: TransactionFilters) => {
      endLink();
      setListFilters(next);
    },
    [endLink]
  );
  const [revenueModalOpen, setRevenueModalOpen] = useState(false);
  const [avgRevenueModalOpen, setAvgRevenueModalOpen] = useState(false);
  /** null until first reconcile against the current analysis month set. */
  const [avgMonthKeys, setAvgMonthKeys] = useState<string[] | null>(null);
  const [draftAvgMonthKeys, setDraftAvgMonthKeys] = useState<string[]>([]);

  // Offer values lifted from OfferCalculator
  const [offerValues, setOfferValues] = useState<OfferValues | null>(null);

  const [acceptLoadingDelay, setAcceptLoadingDelay] = useState(false);
  const [denyLoadingDelay, setDenyLoadingDelay] = useState(false);

  // Dialog open states
  const [acceptOpen, setAcceptOpen] = useState(false);
  const [onboardingNotice, setOnboardingNotice] = useState<string | null>(null);
  const [onboardingNoticeError, setOnboardingNoticeError] = useState(false);
  const [onboardingError, setOnboardingError] = useState<string | null>(null);
  const contractPreview = useContractPreview(borrowerId);
  const startContractPreview = contractPreview.start;
  const [denyOpen, setDenyOpen] = useState(false);
  const [changeDecisionOpen, setChangeDecisionOpen] = useState(false);
  // True when Accept/Deny is triggered from "Edit" (update existing vs create new)
  const [isUpdating, setIsUpdating] = useState(false);

  // Final Decision (Fund / Decline) — Final Underwriting tab only
  const [changeFinalDecisionOpen, setChangeFinalDecisionOpen] = useState(false);
  const [finalDecisionUpdating, setFinalDecisionUpdating] = useState(false);
  const [finalDecisionError, setFinalDecisionError] = useState<string | null>(null);

  const funderGroups = useMemo(
    () => sortByLastPaymentDesc(analysis.funder_groups || []),
    [analysis.funder_groups]
  );
  // Older / partial decisions can omit this even though AnalysisOutput types it
  // as required — slicing undefined crashed the whole AI Analysis / Final UW tab.
  const revenueLines = useMemo(() => analysis.revenue_lines ?? [], [analysis.revenue_lines]);
  const revenueMonths = useMemo(
    () => revenueLines.map((rl) => ({ month: rl.month_abbrev, amount: rl.amount_display })),
    [revenueLines]
  );
  const revenueByAccount = analysis.revenue_by_account ?? [];
  const avgRevenueMonths = useMemo(
    () => buildAvgRevenueMonths(revenueLines, analysis.revenue_bridge, effectiveMonthKeys),
    [revenueLines, analysis.revenue_bridge, effectiveMonthKeys]
  );
  const monthsFingerprint = useMemo(() => avgMonthsFingerprint(analysis), [analysis]);
  // Reset / prune selection when the month set changes; keep keys across amount-only updates.
  useEffect(() => {
    setAvgMonthKeys((prev) => {
      const next = reconcileAvgMonthSelection(avgRevenueMonths, prev);
      if (
        prev != null &&
        prev.length === next.length &&
        prev.every((key, index) => key === next[index])
      ) {
        return prev;
      }
      return next;
    });
  }, [monthsFingerprint, avgRevenueMonths]);

  const resolvedAvgMonthKeys = avgMonthKeys ?? reconcileAvgMonthSelection(avgRevenueMonths, null);
  const effectiveAverageRevenue = useMemo(
    () => averageForMonthKeys(avgRevenueMonths, resolvedAvgMonthKeys),
    [avgRevenueMonths, resolvedAvgMonthKeys]
  );
  const effectiveAverageDisplay = formatAvgRevenueDisplay(effectiveAverageRevenue);

  const openAvgRevenueModal = useCallback(() => {
    setDraftAvgMonthKeys(resolvedAvgMonthKeys);
    setAvgRevenueModalOpen(true);
  }, [resolvedAvgMonthKeys]);

  const toggleDraftAvgMonth = useCallback((monthKey: string) => {
    setDraftAvgMonthKeys((prev) =>
      prev.includes(monthKey) ? prev.filter((k) => k !== monthKey) : [...prev, monthKey].sort()
    );
  }, []);

  const confirmAvgRevenueMonths = useCallback(() => {
    if (draftAvgMonthKeys.length === 0) return;
    setAvgMonthKeys([...draftAvgMonthKeys].sort());
    setAvgRevenueModalOpen(false);
  }, [draftAvgMonthKeys]);

  const mcaGroups = useMemo(
    () => funderGroups.filter((fg) => (fg.position_type ?? "mca") === "mca"),
    [funderGroups]
  );
  const locOrLoanGroups = useMemo(
    () => funderGroups.filter((fg) => fg.position_type === "loc" || fg.position_type === "loan"),
    [funderGroups]
  );
  const [positionSection, setPositionSection] = useState<PositionSection>("mca");
  // Prefer a non-empty section when the selected one has no counterparties.
  useEffect(() => {
    setPositionSection((current) => {
      if (current === "mca" && mcaGroups.length === 0 && locOrLoanGroups.length > 0) {
        return "loc_loan";
      }
      if (current === "loc_loan" && locOrLoanGroups.length === 0 && mcaGroups.length > 0) {
        return "mca";
      }
      return current;
    });
  }, [mcaGroups.length, locOrLoanGroups.length]);
  const visibleFunderGroups = positionSection === "mca" ? mcaGroups : locOrLoanGroups;
  /**
   * Funder-card selection still keys off funder NAME (calendar chips used to);
   * a lender with both an advance and a credit line shares one highlight.
   */
  const calendarTxns = analysis.calendar_transactions || [];

  /** The reviewer's local leverage toggle on one withdrawal, or undefined if none. */
  const getLocalOverride = useCallback(
    (fg: FunderGroup, w: FunderWithdrawal, wIdx: number): boolean | undefined => {
      const key = funderKey(fg);
      const wKey = withdrawalKey(w, wIdx);
      return withdrawalOverrides[key]?.[wKey];
    },
    [withdrawalOverrides]
  );

  const getEffectiveInLeverage = useCallback(
    (fg: FunderGroup, w: FunderWithdrawal, wIdx: number) =>
      withdrawalInLeverage(w, getLocalOverride(fg, w, wIdx)),
    [getLocalOverride]
  );

  /**
   * Same answer as the dot: include_in_leverage (and local overrides) win over
   * raw pull status, so the assessment and accept dialog cannot count a
   * position the card has already dropped from leverage.
   */
  const getEffectiveActive = useCallback(
    (fg: FunderGroup, w: FunderWithdrawal, wIdx: number): boolean =>
      getEffectiveInLeverage(fg, w, wIdx),
    [getEffectiveInLeverage]
  );

  const getEffectivePayout = useCallback(
    (fg: FunderGroup, w: FunderWithdrawal, wIdx: number) =>
      withdrawalMonthlyPayout(
        w,
        cadenceOverrides[funderKey(fg)]?.[withdrawalKey(w, wIdx)],
        businessDaysPerMonth
      ),
    [cadenceOverrides, businessDaysPerMonth]
  );

  const payoutByFunder = useMemo(() => {
    const result: { id: string; funder: string; monthlyPayout: number }[] = [];

    for (const fg of funderGroups) {
      let funderTotal = 0;
      for (let i = 0; i < fg.withdrawals.length; i++) {
        const w = fg.withdrawals[i];
        if (!getEffectiveInLeverage(fg, w, i)) continue;

        funderTotal += getEffectivePayout(fg, w, i);
      }
      if (funderTotal > 0) {
        const id = funderKey(fg);
        result.push({
          id,
          funder:
            fg.position_type === "loc"
              ? `${fg.name} (LOC)`
              : fg.position_type === "loan"
                ? `${fg.name} (Loan)`
                : fg.name,
          monthlyPayout: funderTotal,
        });
      }
    }

    return result.sort((a, b) => b.monthlyPayout - a.monthlyPayout);
  }, [funderGroups, getEffectiveInLeverage, getEffectivePayout]);

  // Drop local toggles that already match the server so a CorrectionsPanel
  // change (or any other recompute) is not outranked by a stale optimistic flip.
  useEffect(() => {
    const groups = funderGroups.map((fg) => ({
      funderKey: funderKey(fg),
      withdrawals: fg.withdrawals.map((w, i) => ({
        withdrawalKey: withdrawalKey(w, i),
        include_in_leverage: w.include_in_leverage,
        is_active: w.is_active,
      })),
    }));
    setWithdrawalOverrides((prev) => pruneMatchingLeverageOverrides(prev, groups));
  }, [funderGroups]);

  const { effectiveLeverage, effectiveMonthlyPayout } = useMemo(() => {
    let totalMonthlyPayout = 0;
    for (const fg of funderGroups) {
      for (let i = 0; i < fg.withdrawals.length; i++) {
        const w = fg.withdrawals[i];
        if (getEffectiveInLeverage(fg, w, i)) {
          totalMonthlyPayout += getEffectivePayout(fg, w, i);
        }
      }
    }
    const avgRev = effectiveAverageRevenue || 0;
    const leverage = avgRev > 0 ? (totalMonthlyPayout / avgRev) * 100 : 0;
    return { effectiveLeverage: leverage, effectiveMonthlyPayout: totalMonthlyPayout };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [funderGroups, getEffectiveInLeverage, getEffectivePayout, effectiveAverageRevenue]);

  // What the assistant sees of this screen when the user sends from it.
  const analysisSlot = useMemo(
    () =>
      analysisPageSlot({
        source: transactionSource,
        avgMonthKeys: resolvedAvgMonthKeys,
        defaultAvgMonthKeys: reconcileAvgMonthSelection(avgRevenueMonths, null),
        averageRevenue: effectiveAverageRevenue,
        leveragePct: effectiveLeverage,
        monthlyPayout: effectiveMonthlyPayout,
        leverageWarnPct,
        funderGroups,
        localOverride: getLocalOverride,
      }),
    [
      transactionSource,
      resolvedAvgMonthKeys,
      avgRevenueMonths,
      effectiveAverageRevenue,
      effectiveLeverage,
      effectiveMonthlyPayout,
      leverageWarnPct,
      funderGroups,
      getLocalOverride,
    ]
  );
  useAgentPageState("analysis", analysisSlot);
  useAgentPageState("offer", isFunderStaff && offerValues ? offerPageSlot(offerValues) : null);

  const toggleFunderFilter = useCallback((name: string) => {
    setCalendarFilters((prev) => {
      const funders = prev.funders.includes(name)
        ? prev.funders.filter((f) => f !== name)
        : [...prev.funders, name];
      return { ...prev, funders };
    });
  }, []);

  const handleFunderNameClick = useCallback(
    (name: string) => {
      // Position filters replace list filters (e.g. Recent Revenue credit/month).
      endLink();
      setListFilters(defaultFilters);
      setKeyFilter(null);
      toggleFunderFilter(name);
      setSelectedFilter((prev) =>
        prev?.type === "funder" && prev.name === name ? null : { type: "funder", name }
      );
    },
    [toggleFunderFilter, endLink]
  );

  const handlePositionClick = useCallback(
    (name: string, kind: "withdrawal" | "deposit", amount: number, date?: string) => {
      // Position filters replace list filters (e.g. Recent Revenue credit/month).
      endLink();
      setListFilters(defaultFilters);
      setKeyFilter(null);
      setSelectedFilter((prev) => {
        const isSame =
          prev?.type === kind &&
          prev.name === name &&
          prev.amount === amount &&
          (kind === "deposit" ? (prev as { date: string }).date === date : true);
        return isSame
          ? null
          : kind === "withdrawal"
            ? { type: "withdrawal" as const, name, amount }
            : { type: "deposit" as const, name, amount, date: date ?? "" };
      });
    },
    [endLink]
  );

  const keyFilterSet = useMemo(() => (keyFilter ? new Set(keyFilter) : null), [keyFilter]);

  const filteredBorrowerTxns = useMemo(() => {
    return transactions.filter((tx) => {
      if (keyFilterSet && !(tx.override_key && keyFilterSet.has(tx.override_key))) return false;
      if (selectedFilter) {
        // The card was built from rows grouped by canonical funder, case- and
        // suffix-insensitively; match the same way or "UFCE" hides "Ufce".
        if (
          !isSameFunder(tx.canonical_counterparty_name ?? tx.counterparty_name, selectedFilter.name)
        ) {
          return false;
        }
        if (selectedFilter.type === "withdrawal") {
          if (tx.transaction_type !== "debit") return false;
          if (Math.abs(Math.abs(tx.amount) - selectedFilter.amount) > 1.0) return false;
        }
        if (selectedFilter.type === "deposit") {
          if (tx.transaction_type !== "credit") return false;
          if (tx.date !== selectedFilter.date) return false;
        }
      }

      const f = listFilters;

      if (f.search) {
        const q = f.search.toLowerCase();
        const match =
          tx.description?.toLowerCase().includes(q) ||
          tx.memo?.toLowerCase().includes(q) ||
          tx.tags_display?.toLowerCase().includes(q) ||
          tx.counterparty_name?.toLowerCase().includes(q);
        if (!match) return false;
      }

      if (f.transaction_type !== "all" && tx.transaction_type !== f.transaction_type) {
        return false;
      }

      if (f.sub_category && f.sub_category !== "all" && tx.category !== f.sub_category) {
        return false;
      }

      if (f.tag && !tx.tags_display?.toLowerCase().includes(f.tag.toLowerCase())) {
        return false;
      }

      if (f.account_name && tx.account_name !== f.account_name) return false;
      if (f.account_number && tx.account_number !== f.account_number) return false;

      if (f.date_from && tx.date < f.date_from) return false;
      if (f.date_to && tx.date > f.date_to) return false;

      if (f.amount_min && Math.abs(tx.amount) < parseFloat(f.amount_min)) return false;
      if (f.amount_max && Math.abs(tx.amount) > parseFloat(f.amount_max)) return false;

      return true;
    });
  }, [transactions, listFilters, selectedFilter, keyFilterSet]);

  const listAccountNames = useMemo(
    () => [...new Set(transactions.map((t) => t.account_name).filter(Boolean))].sort() as string[],
    [transactions]
  );

  const listAccountNumbers = useMemo(
    () =>
      [...new Set(transactions.map((t) => t.account_number).filter(Boolean))].sort() as string[],
    [transactions]
  );

  const listTags = useMemo(() => {
    const s = new Set<string>();
    transactions.forEach((t) =>
      t.tags_display
        ?.split(", ")
        .filter(Boolean)
        .forEach((tag) => s.add(tag))
    );
    return [...s].sort();
  }, [transactions]);

  const listSubCategories = useMemo(
    () => [...new Set(transactions.map((t) => t.category).filter(Boolean))].sort() as string[],
    [transactions]
  );

  const listColumnsFull: TableColumn<BorrowerTransaction>[] = [
    {
      label: "",
      width: 28,
      sortFn: () => 0,
    },
    {
      label: "Acc #",
      width: "4.5rem",
      sortFn: (tx) => tx.account_number ?? "",
    },
    {
      label: "Description",
      // Flexible leftover after fixed columns (Category widened for LOAN_PAYMENT + select).
      sortFn: (tx) => tx.description || "",
    },
    {
      label: "Date",
      width: "3.75rem",
      sortFn: (tx) => tx.date || "",
    },
    {
      label: "Amount",
      width: "6.25rem",
      sortFn: (tx) => tx.amount,
      align: "right",
    },
    {
      label: "Balance",
      width: "5.5rem",
      sortFn: (tx) => tx.balance ?? 0,
      align: "right",
    },
    {
      label: "Category",
      // Widest display label is CREDIT_CARD_PAYMENT ("CARD_PAYMENT"), plus the chevron.
      width: "10rem",
      sortFn: (tx) => tx.category ?? "",
      align: "right",
    },
    ...(showEnrichedColumns
      ? ([
          {
            label: "Revenue",
            // Room for the TR / NTR dot, label and select chevron.
            width: "5rem",
            sortFn: (tx) => (tx.is_revenue ? 1 : 0),
            align: "center",
          },
          {
            label: "Counterparty",
            width: "8rem",
            sortFn: (tx) => tx.counterparty_name ?? "",
          },
        ] satisfies TableColumn<BorrowerTransaction>[])
      : []),
  ];

  const handleToggleActive = useCallback(
    (fg: FunderGroup, wIdx: number) => {
      const w = fg.withdrawals[wIdx];
      if (!w) return;
      const key = funderKey(fg);
      const wKey = withdrawalKey(w, wIdx);
      const requestKey = `${key}|${wKey}`;
      const persist = onPersistActiveToggle;
      const overrideKey = w.override_key;

      // Allow rapid re-clicks: generation tokens ignore stale settles so a
      // second edit while the first is in flight still lands.
      const current = getEffectiveInLeverage(fg, w, wIdx);
      const previous = withdrawalOverrides[key]?.[wKey];
      const generation = (toggleRequestGen.current.get(requestKey) ?? 0) + 1;
      toggleRequestGen.current.set(requestKey, generation);

      // Recorded locally even when it is also being saved: a hand-picked cadence
      // carries a position into leverage on its own, so without an explicit note
      // of the click the dot would light straight back up and the toggle would
      // look broken. It doubles as the optimistic update while the save is in
      // flight.
      setWithdrawalOverrides((prev) => setOverride(prev, key, wKey, !current));

      if (!persist || !overrideKey) return;

      persist(overrideKey, !current)
        .then(() => {
          if (toggleRequestGen.current.get(requestKey) !== generation) return;
          // Server is authoritative again; keeping the flip would fight a later
          // CorrectionsPanel change on the same field.
          setWithdrawalOverrides((prev) => setOverride(prev, key, wKey, undefined));
        })
        .catch(() => {
          if (toggleRequestGen.current.get(requestKey) !== generation) return;
          // Restore the value the click replaced rather than flipping back:
          // re-flipping would write an override where there was none, pinning
          // the position to a state nobody chose. The caller surfaces the error.
          setWithdrawalOverrides((prev) => setOverride(prev, key, wKey, previous));
        });
    },
    [getEffectiveInLeverage, onPersistActiveToggle, withdrawalOverrides]
  );

  const handleChangeFrequency = useCallback(
    (fg: FunderGroup, wIdx: number, cadence: ResolvableCadence) => {
      const w = fg.withdrawals[wIdx];
      if (!w) return;
      const key = funderKey(fg);
      const wKey = withdrawalKey(w, wIdx);
      const requestKey = `freq|${key}|${wKey}`;
      const persist = onPersistFrequency;
      const overrideKey = w.override_key;
      const current =
        cadenceOverrides[key]?.[wKey] ??
        ((RESOLVABLE_CADENCES as readonly string[]).includes(w.frequency)
          ? (w.frequency as ResolvableCadence)
          : null);

      // No-op when the user re-selects the already-effective frequency.
      if (current === cadence) return;

      const previous = cadenceOverrides[key]?.[wKey];
      const generation = (toggleRequestGen.current.get(requestKey) ?? 0) + 1;
      toggleRequestGen.current.set(requestKey, generation);

      setCadenceOverrides((prev) => setOverride(prev, key, wKey, cadence));

      if (!persist || !overrideKey) return;

      persist(overrideKey, cadence)
        .then(() => {
          if (toggleRequestGen.current.get(requestKey) !== generation) return;
          setCadenceOverrides((prev) => setOverride(prev, key, wKey, undefined));
        })
        .catch(() => {
          if (toggleRequestGen.current.get(requestKey) !== generation) return;
          setCadenceOverrides((prev) => setOverride(prev, key, wKey, previous));
        });
    },
    [cadenceOverrides, onPersistFrequency]
  );

  const handleRevenueMonthClick = useCallback(
    (monthAbbrev: string) => {
      const matchKey = effectiveMonthKeys.find((k) => monthKeyToAbbrev(k) === monthAbbrev);
      if (!matchKey) return;
      // Replace any EXISTING POSITIONS / funder filters with credits for this month.
      endLink();
      setSelectedFilter(null);
      setKeyFilter(null);
      setCalendarFilters((prev) => ({ ...prev, funders: [] }));
      setListFilters(monthCreditsFilters(matchKey));
    },
    [effectiveMonthKeys, endLink]
  );

  // The navigation whose link is on screen: the highlight scrolls once per opening.
  const [linkShownKey, setLinkShownKey] = useState<string | null>(null);
  const link = listLink?.link ?? null;
  const linkKey = listLink?.key ?? null;

  const applyLink = useCallback(
    (next: AnalysisListLink) => {
      const filter = next.filter;
      let selected: FunderFilter | null = null;
      if (filter?.kind === "position") {
        for (const fg of funderGroups) {
          const w = fg.withdrawals.find((wd) => wd.override_key === filter.position);
          if (w) {
            selected = { type: "withdrawal", name: fg.name, amount: w.amount };
            break;
          }
        }
      }
      setListFilters(filter?.kind === "month" ? monthCreditsFilters(filter.month) : defaultFilters);
      setSelectedFilter(selected);
      setKeyFilter(filter?.kind === "keys" ? filter.keys : null);
      setCalendarFilters((prev) => ({ ...prev, funders: [] }));
    },
    [funderGroups]
  );

  // Each navigation applies the address's link; one that drops it (Back) restores the plain
  // list. A layout effect, so a linked list never paints unfiltered first.
  useLayoutEffect(() => {
    if (link) {
      applyLink(link);
      linkApplied.current = true;
      setLinkShownKey(linkKey);
    } else if (linkApplied.current) {
      linkApplied.current = false;
      setLinkEnds((count) => count + 1);
      setListFilters(defaultFilters);
      setSelectedFilter(null);
      setKeyFilter(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once per navigation, not per render
  }, [linkKey]);

  const highlightKeys = useMemo(() => new Set(link?.highlight ?? []), [link]);
  const reveal = useMemo(
    () =>
      highlightKeys.size > 0 && linkShownKey != null && linkShownKey === linkKey
        ? {
            key: linkShownKey,
            match: (tx: BorrowerTransaction) =>
              !!tx.override_key && highlightKeys.has(tx.override_key),
          }
        : undefined,
    [highlightKeys, linkShownKey, linkKey]
  );

  const truncate = (value: string | null | undefined, max = 50) => {
    if (!value) return "-";
    return value.length > max ? `${value.slice(0, max)}…` : value;
  };

  const renderBorrowerTxnRow = (tx: BorrowerTransaction) => {
    const canEditClassification = !readOnly && !!onSaveTransactionOverride && !!tx.override_key;

    const categoryCell = (): CellDef => {
      if (!canEditClassification || !onSaveTransactionOverride) {
        return {
          value: categoryDisplayLabel(tx.category || "-"),
          align: "right",
          fontSize: "0.75rem",
        };
      }
      return {
        value: (
          <CategoryOverrideSelect
            value={tx.category || ""}
            onChange={(next) =>
              saveTransactionClassification(onSaveTransactionOverride, tx, "txn_type", next)
            }
          />
        ),
      };
    };

    const revenueCell = (): CellDef => {
      const label = revenueLabel(tx);
      if (label === "-") {
        return { value: "-", align: "center", color: "text.secondary", fontSize: "0.75rem" };
      }
      if (!canEditClassification || !onSaveTransactionOverride) {
        return {
          type: "chip",
          value: label,
          color: tx.is_revenue ? "success" : "warning",
          variant: "outlined",
        };
      }
      return {
        value: (
          <RevenueOverrideSelect
            isRevenue={!!tx.is_revenue}
            onChange={(next) =>
              saveTransactionClassification(onSaveTransactionOverride, tx, "rev", next)
            }
          />
        ),
      };
    };

    const cells: CellDef[] = [
      // Account #
      { value: tx.account_number || "-", fontWeight: 500, fontSize: "0.75rem" },

      // Description (truncated) — shorter than before so Category can take more width.
      {
        value: truncate(tx.description, 28),
        fontSize: "0.75rem",
      },

      // Date (formatted "MMM d")
      {
        value: tx.date ? format(parseISO(tx.date), "MMM d") : "-",
        fontSize: "0.75rem",
      },
      // Amount with direction arrow/color
      {
        type: "amount",
        amount: tx.amount,
        transactionType: tx.transaction_type,
      },

      // Balance
      {
        value: tx.balance != null ? formatCurrency(tx.balance) : "-",
        align: "right",
        color: "text.secondary",
        fontSize: "0.75rem",
      },

      categoryCell(),

      // Revenue/Counterparty — only populated by the experimental AI Analysis
      // (EXP) run's enriched transactions; gated by showEnrichedColumns.
      ...(showEnrichedColumns
        ? [
            revenueCell(),
            { value: truncate(tx.counterparty_name, 25), fontSize: "0.75rem" } as CellDef,
          ]
        : []),
    ];

    const detail: DetailDef = {
      type: "fields",
      fields: [
        { label: "Description:", value: tx.description || "-" },
        { label: "Memo:", value: tx.memo || "-" },
        ...(tx.counterparty_name ? [{ label: "Counterparty:", value: tx.counterparty_name }] : []),
      ],
    };

    const rowKey =
      tx.id ?? `${tx.account_number}-${tx.date}-${tx.amount}-${tx.balance}-${tx.category}`;

    return (
      <ExpandableRow
        key={rowKey}
        isExpanded={expandedRowId === rowKey}
        onToggle={() => setExpandedRowId((current) => (current === rowKey ? null : rowKey))}
        cells={cells}
        detail={detail}
        compact
        highlighted={!!tx.override_key && highlightKeys.has(tx.override_key)}
      />
    );
  };

  // Build AssessmentPositions from funder_groups for submission
  const buildPositions = useCallback((): AssessmentPosition[] => {
    return funderGroups.flatMap((fg) =>
      fg.withdrawals.map((w, wIdx) => ({
        id: `${funderKey(fg)}-${wIdx}`,
        funder: fg.name,
        fundingAmount: fg.total_deposits > 0 ? String(Math.round(fg.total_deposits)) : "N/A",
        payment: w.amount,
        frequency: w.frequency as PullFrequency,
        tag: "new" as const,
        type: fg.position_type === "loan" ? "loc" : (fg.position_type ?? "mca"),
        active: getEffectiveActive(fg, w, wIdx),
        month: "Existing",
      }))
    );
  }, [funderGroups, getEffectiveActive]);

  // Effective active positions for AcceptDialog display (respects user toggles)
  const activePositionsForDialog = useMemo(
    (): ActivePosition[] =>
      funderGroups.flatMap((fg) =>
        fg.withdrawals
          .map((w, wIdx) => ({
            funder: fg.name,
            amount: w.amount,
            frequency: w.frequency,
            active: getEffectiveActive(fg, w, wIdx),
          }))
          .filter((p) => p.active)
      ),
    [funderGroups, getEffectiveActive]
  );

  // Ordinal position string derived from active position count (reflects user toggles)
  const computedOrdinalPosition = useMemo(() => {
    const n = activePositionsForDialog.length + 1;
    const suffix = n === 1 ? "st" : n === 2 ? "nd" : n === 3 ? "rd" : "th";
    return `Coming in as the ${n}${suffix} position`;
  }, [activePositionsForDialog]);

  // Initial offer values for OfferCalculator — only when an accepted assessment exists
  const offerInitialValues = useMemo(() => {
    if (!existingAssessment || existingAssessment.accepted !== true) return undefined;
    return {
      fundingAmount: existingAssessment.fundingAmount,
      factor: existingAssessment.factor,
      installment:
        existingAssessment.merchantInstallment ?? existingAssessment.merchant_installment,
      term: existingAssessment.term,
      frequency: existingAssessment.frequency,
      addedLeverage: existingAssessment.addedLeverage,
    };
  }, [existingAssessment]);

  /** Live offer-calc payment/term/freq for balance coloring; fall back to accepted assessment. */
  const balanceOffer = useMemo(() => {
    if (offerValues && Number(offerValues.installment) > 0) {
      return {
        payment: Number(offerValues.installment),
        term: Number(offerValues.term) || 0,
        frequency: offerValues.frequency || "daily",
      };
    }
    const installment =
      existingAssessment?.merchantInstallment ?? existingAssessment?.merchant_installment;
    if (existingAssessment?.accepted === true && Number(installment) > 0) {
      return {
        payment: Number(installment),
        term: Number(existingAssessment.term) || 0,
        frequency: existingAssessment.frequency || "daily",
      };
    }
    return null;
  }, [offerValues, existingAssessment]);

  const possibleMissedCount = useMemo(() => {
    if (!balanceOffer) return 0;
    return countPossibleMissedFromOffer(
      analysis.daily_balances ?? [],
      balanceOffer.payment,
      balanceOffer.frequency,
      balanceOffer.term
    );
  }, [analysis.daily_balances, balanceOffer]);

  const scrollToDailyBalances = useCallback(() => {
    document
      .getElementById(DAILY_BALANCES_SECTION_ID)
      ?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, []);

  const onboardingDecision = approvalOnboardingDecision({
    startDefaultOnboarding,
    alreadyAccepted: existingAssessment?.accepted === true,
    autoSendContractsEnabled,
    canSendMerchantTasks: actorCanSendMerchantTasks,
  });
  const showSendContractOption = onboardingDecision === "dual";

  useEffect(() => {
    if (!acceptOpen || !showSendContractOption || borrowerId == null) {
      setOnboardingNotice(null);
      setOnboardingNoticeError(false);
      return;
    }
    let cancelled = false;
    setOnboardingNotice(null);
    setOnboardingNoticeError(false);
    merchantFlowService
      .defaults(borrowerId, "onboarding")
      .then((evaluation) => {
        if (cancelled) return;
        const plan = planDefaultOnboarding(evaluation);
        setOnboardingNotice(plan.notice);
        setOnboardingNoticeError(plan.error);
      })
      .catch(() => {
        if (cancelled) return;
        setOnboardingNotice("");
        setOnboardingNoticeError(false);
      });
    return () => {
      cancelled = true;
    };
  }, [acceptOpen, showSendContractOption, borrowerId]);

  const handleAcceptConfirm = useCallback(
    async (notes: string, sendContract: boolean) => {
      if (!offerValues || !borrowerId) return;

      setAcceptLoadingDelay(true);
      setOnboardingError(null);

      const data = {
        accepted: true as const,
        borrower: borrowerId,
        notes,
        defaultHistory: "",
        sources: "",
        brokerCompany: "",
        brokerName: "",
        quality: "" as const,
        nMonths: revenueLines.length,
        statements: revenueLines.map((rl) => rl.amount),
        positions: buildPositions(),
        fundingAmount: offerValues.fundingAmount,
        factor: offerValues.factor,
        merchantInstallment: offerValues.installment,
        term: offerValues.term,
        frequency: offerValues.frequency as PullFrequency,
        addedLeverage: offerValues.addedLeverage,
        denyReason: [] as DenyReason[],
      };
      try {
        if (existingAssessment) {
          await update(existingAssessment.id, data);
          setIsUpdating(false);
        } else {
          await submit(data);
        }
        if (sendContract) {
          try {
            // The offer is saved above. Plan again so contract terms that
            // come from that assessment are present, then preview or send.
            const evaluation = await merchantFlowService.defaults(borrowerId, "onboarding");
            const plan = planDefaultOnboarding(evaluation);
            if (plan.request && hasMainContract(evaluation)) {
              await startContractPreview(plan.request);
              setAcceptLoadingDelay(false);
              setAcceptOpen(false);
              return;
            }
            if (plan.request) {
              await merchantFlowService.send(borrowerId, plan.request);
              applyInvalidations(queryClient, INVALIDATES.send(borrowerId));
            } else if (plan.error) {
              setOnboardingError(plan.notice);
            }
          } catch (err) {
            setOnboardingError(
              isAxiosError(err) ? handleApiError(err) : "The onboarding flow was not sent."
            );
          }
        }
        setTimeout(() => {
          setAcceptLoadingDelay(false);
          setAcceptOpen(false);
        }, 2000);
      } catch {
        // error shown via hook state — always clear the dialog spinner so a
        // failed PUT (e.g. 403) does not leave Approve stuck loading forever.
        setAcceptLoadingDelay(false);
      }
    },
    [
      offerValues,
      borrowerId,
      revenueLines,
      buildPositions,
      existingAssessment,
      update,
      submit,
      queryClient,
      startContractPreview,
    ]
  );

  /** Close the preview modal and void it. The banner sits behind the modal, so say why here. */
  const discardOnboardingPreview = async (reason: string) => {
    await contractPreview.discard();
    setOnboardingError(reason);
  };

  const confirmOnboardingPreview = async () => {
    if (!borrowerId) return;
    setOnboardingError(null);
    try {
      await contractPreview.confirm((request) => merchantFlowService.send(borrowerId, request));
      applyInvalidations(queryClient, INVALIDATES.send(borrowerId));
      contractPreview.release();
    } catch (err) {
      const failure = classifySendFailure(err);
      // Staff (or a reachable DocuSeal) can still fix the preview: keep it open.
      if (failure.kind === "preview") {
        contractPreview.showError(failure.message);
        return;
      }
      await discardOnboardingPreview(
        isAxiosError(err) ? handleApiError(err) : "The onboarding flow was not sent."
      );
    }
  };

  const handleDenyConfirm = useCallback(
    async (reasons: DenyReason[], notes: string) => {
      if (!borrowerId) return;

      setDenyLoadingDelay(true);

      const data = {
        accepted: false as const,
        borrower: borrowerId,
        notes: "",
        defaultHistory: "",
        sources: "",
        brokerCompany: "",
        brokerName: "",
        quality: "" as const,
        nMonths: revenueLines.length,
        statements: revenueLines.map((rl) => rl.amount),
        positions: buildPositions(),
        denyReason: reasons,
        denyMoreInfo: notes,
        fundingAmount: undefined,
        factor: 1.49,
        merchantInstallment: undefined,
        term: 60,
        frequency: "daily" as PullFrequency,
        addedLeverage: 0,
      };
      try {
        if (existingAssessment) {
          await update(existingAssessment.id, data);
          setIsUpdating(false);
        } else {
          await submit(data);
        }

        setTimeout(() => {
          setDenyLoadingDelay(false);
          setDenyOpen(false);
        }, 2000);
      } catch {
        // Same as accept: do not leave the dialog spinner stuck on API errors.
        setDenyLoadingDelay(false);
      }
    },
    [borrowerId, revenueLines, buildPositions, existingAssessment, update, submit]
  );

  const assessmentOutcome: DecisionOutcome | null = !existingAssessment
    ? null
    : existingAssessment.accepted === true
      ? "approved"
      : existingAssessment.accepted === false
        ? "declined"
        : "pending";

  const headerLayout = HEADER_LAYOUT[topRightAction ?? "none"];

  // The dot / frequency dropdown only edit where a handler exists to persist;
  // elsewhere they stay display-only (broker safety).
  const activeToggleDisabled = readOnly && !onPersistActiveToggle;
  const frequencyEditable = !!onPersistFrequency || enableCadenceResolution;
  const frequencyDisabled = readOnly && !onPersistFrequency && !enableCadenceResolution;

  const renderFunderCard = (fg: FunderGroup) => {
    const key = funderKey(fg);
    return (
      <FunderCard
        key={key}
        funder={fg}
        isSelected={calendarFilters.funders.includes(fg.name)}
        onNameClick={handleFunderNameClick}
        onHighlightDates={() => undefined}
        onToggleActive={(_name, wIdx) => handleToggleActive(fg, wIdx)}
        onWithdrawalClick={(name, amount) => handlePositionClick(name, "withdrawal", amount)}
        onDepositClick={(name, amount, date) => handlePositionClick(name, "deposit", amount, date)}
        calendarTransactions={calendarTxns}
        withdrawalActiveOverrides={withdrawalOverrides[key] || {}}
        cadenceOverrides={frequencyEditable ? cadenceOverrides[key] || {} : undefined}
        onChangeFrequency={
          frequencyEditable
            ? (wIdx, cadence) => handleChangeFrequency(fg, wIdx, cadence)
            : undefined
        }
        activeToggleDisabled={activeToggleDisabled}
        frequencyDisabled={frequencyDisabled}
      />
    );
  };

  // Final Decision (Fund / Decline) — drives the Final Underwriting tab's
  // dedicated status column. Independent of the assessment's accept/decline.
  const finalAppStatus = borrower?.app_status;
  const isFinalDecisionFunded =
    finalAppStatus === "funded" ||
    (!!finalAppStatus && POST_FUNDING_APP_STATUSES.includes(finalAppStatus));
  const isFinalDecisionDeclined = finalAppStatus === "declined";
  const isFinalUwPending = finalAppStatus === "final_underwriting";
  // Pencil-editable only right at the outcome, not once the deal has progressed
  // further (e.g. into repayment) — matches the assessment Decision pattern.
  const canEditFinalDecision = finalAppStatus === "funded" || finalAppStatus === "declined";
  const finalOutcome: DecisionOutcome | null = isFinalDecisionFunded
    ? "funded"
    : isFinalDecisionDeclined
      ? "declined"
      : null;
  // Fund / Decline sit as a centred pair; otherwise the pane holds a block that fills it.
  // View PDF beside it takes the same box either way.
  const finalDecisionShowsActions = canActOnFinalDecision && isFinalUwPending;

  const handleSetFinalDecision = useCallback(
    async (status: "funded" | "declined") => {
      setFinalDecisionUpdating(true);
      setFinalDecisionError(null);
      try {
        await patch({ app_status: status });
        setChangeFinalDecisionOpen(false);
        setFinalDecisionError(null);
      } catch (err) {
        const message =
          err instanceof AxiosError
            ? handleApiError(err)
            : err instanceof Error
              ? err.message
              : "Could not update final decision.";
        setFinalDecisionError(message);
      } finally {
        setFinalDecisionUpdating(false);
      }
    },
    [patch]
  );

  const fundFinal = useCallback(
    () => void handleSetFinalDecision("funded"),
    [handleSetFinalDecision]
  );
  const declineFinal = useCallback(
    () => void handleSetFinalDecision("declined"),
    [handleSetFinalDecision]
  );
  const openChangeFinalDecision = useCallback(() => {
    setFinalDecisionError(null);
    setChangeFinalDecisionOpen(true);
  }, []);
  const openAccept = useCallback(() => setAcceptOpen(true), []);
  const openDeny = useCallback(() => setDenyOpen(true), []);
  const openChangeDecision = useCallback(() => setChangeDecisionOpen(true), []);
  const openRevenueModal = useCallback(() => setRevenueModalOpen(true), []);
  // The Recent Revenue tile whose month the transactions list is filtered to (credits only).
  const activeRevenueMonth = useMemo(() => {
    if (listFilters.transaction_type !== "credit") return null;
    const line = revenueLines.slice(-3).find((rl) => {
      const monthKey = effectiveMonthKeys.find((k) => monthKeyToAbbrev(k) === rl.month_abbrev);
      return !!monthKey && listFilters.date_from?.startsWith(monthKey);
    });
    return line?.month_abbrev ?? null;
  }, [revenueLines, effectiveMonthKeys, listFilters]);

  return (
    <TooltipProvider delayDuration={150}>
      <div
        className="font-sans text-xs antialiased [-webkit-font-smoothing:antialiased]"
        style={{ letterSpacing: "-0.01em" }}
      >
        {onboardingError && (
          <Alert variant="destructive" className="mb-3">
            <AlertDescription>
              Approved, but the onboarding flow was not sent. {onboardingError}
            </AlertDescription>
          </Alert>
        )}
        {revenueByAccount.length >= 2 && (
          <div
            className="mb-4 overflow-hidden rounded-lg border border-border/60 bg-card shadow-sm"
            data-testid="revenue-by-account"
          >
            <div className="px-4 py-3">
              <p className={cn(HEADER_LABEL, "mb-2")}>Revenue by account</p>
              <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                {revenueByAccount.map((account) => (
                  <div
                    key={account.account}
                    className="rounded-md border border-border/50 px-3 py-2"
                  >
                    <p className="font-medium text-foreground">
                      {[account.bank_name, account.account_number].filter(Boolean).join(" · ") ||
                        account.account}
                    </p>
                    <p className="text-muted-foreground">
                      Real revenue {account.true_revenue_display}
                    </p>
                    <p className="text-muted-foreground">Deductions {account.deductions_display}</p>
                    <p className="text-muted-foreground">{account.share_of_revenue}% of revenue</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ===== BOARD: header cards per HEADER_LAYOUT, then Positions | Transactions ===== */}
        <div className="mb-3 [container:board/inline-size]">
          <div className={cn("grid gap-3", headerLayout.header)}>
            {/* ── 1–2. Revenue, Leverage + Payout ── */}
            <DecisionStrip
              placement="board"
              revenueLines={revenueLines}
              avgRevenue={avgRevenueMonths.length > 0 ? effectiveAverageDisplay : null}
              missingAvgMonths={analysis.missing_avg_revenue_months ?? NO_MONTHS}
              leveragePct={effectiveLeverage}
              targetPct={leverageWarnPct}
              monthlyPayout={effectiveMonthlyPayout}
              payoutByFunder={payoutByFunder}
              activeMonth={activeRevenueMonth}
              onMonthClick={handleRevenueMonthClick}
              onShowAllRevenue={openRevenueModal}
              onAvgRevenueClick={openAvgRevenueModal}
            />

            {/* ── 3. Offer Builder ── */}
            <div
              className={cn(
                "flex flex-col justify-center rounded-lg border border-border/60 bg-card px-4 py-3 shadow-sm [grid-area:calc]",
                headerLayout.calc
              )}
            >
              <OfferCalculator
                key={existingAssessment?.id ?? "none"}
                currentLeveragePct={effectiveLeverage}
                averageRevenue={effectiveAverageRevenue}
                inline
                id={borrowerId}
                onOfferChange={setOfferValues}
                initialValues={offerInitialValues}
                readOnly={readOnly || !isUnderwritingActor}
                possibleMissedCount={possibleMissedCount}
                onPossibleMissedNavigate={scrollToDailyBalances}
              />
            </div>

            {/* ── 4. Decision panes ── */}
            <div
              className={cn(
                "grid overflow-hidden rounded-lg border border-border/60 bg-card shadow-sm [grid-area:panes]",
                headerLayout.panes
              )}
            >
              {topRightAction !== "preview-plaid-pdf" && (
                <DecisionColumn
                  kind="underwriting"
                  layout="stacked"
                  outcome={assessmentOutcome}
                  canAct={canActOnAssessment}
                  onApprove={openAccept}
                  onDecline={openDeny}
                  // Hide the edit pencil unless the actor can underwrite
                  onEdit={existingAssessment && canActOnAssessment ? openChangeDecision : undefined}
                  approveDisabled={!offerValues?.fundingAmount}
                />
              )}
              {topRightAction === "preview-plaid-pdf" && (
                <div className={HEADER_PANE}>
                  <p className={cn(HEADER_LABEL, "whitespace-nowrap")}>Plaid Asset Report</p>
                  <div className="mt-2 flex flex-1 flex-col items-stretch justify-center">
                    {pdfSlotMode === "manual-login" ? (
                      <div className="flex flex-1 cursor-default select-none items-center justify-center rounded-md border border-yellow-500/40 bg-yellow-500/10 px-3 text-center text-xs font-semibold leading-tight text-yellow-700 dark:text-yellow-500">
                        Moved to Final via Manual Login
                      </div>
                    ) : pdfSlotMode === "no-report" ? (
                      <div className="flex flex-1 cursor-default select-none items-center justify-center rounded-md bg-muted px-3 text-center text-xs font-medium leading-tight text-muted-foreground">
                        No Plaid Report
                      </div>
                    ) : (
                      <Button
                        type="button"
                        variant="default"
                        size="sm"
                        className={cn(
                          "flex-1",
                          finalDecisionShowsActions && "h-[4.5rem] flex-none"
                        )}
                        onClick={() => onPreviewPlaidPdf?.()}
                        disabled={!borrower?.plaid_asset_report_pdf_url}
                      >
                        View PDF
                      </Button>
                    )}
                  </div>
                </div>
              )}
              {borrowerId && topRightAction === "preview-plaid-pdf" && (
                <DecisionColumn
                  kind="funding"
                  layout="stacked"
                  className={headerLayout.pane}
                  outcome={finalOutcome}
                  canAct={canActOnFinalDecision}
                  awaiting={!isFinalUwPending}
                  onApprove={fundFinal}
                  onDecline={declineFinal}
                  onEdit={
                    canEditFinalDecision && canActOnFinalDecision
                      ? openChangeFinalDecision
                      : undefined
                  }
                  busy={finalDecisionUpdating}
                  error={changeFinalDecisionOpen ? null : finalDecisionError}
                />
              )}
            </div>
          </div>

          {/* Side by side from 40rem; stacked on phones, where Transactions needs the width */}
          <div className="mt-3 grid gap-3 [@container_board_(min-width:40rem)]:grid-cols-[minmax(200px,25%)_minmax(0,1fr)]">
            {/* ── Positions ── */}
            {/* No intrinsic height, only an abspos scroller: the floor sizes it when it is
              alone in a row; beside Transactions it fills that row, so a long list scrolls. */}
            <div className="relative min-h-[600px] min-w-0">
              <div className="absolute inset-0 flex flex-col overflow-y-auto overflow-x-hidden rounded-md border border-border bg-muted/40">
                {funderGroups.length > 0 ? (
                  <>
                    {/* Flip between advances and credit lines / loans so long lists
                      don't force scrolling past one section to reach the other. */}
                    <div className="sticky top-0 z-10 border-b border-border bg-muted p-0.5">
                      <ToggleGroup
                        type="single"
                        value={positionSection}
                        onValueChange={(v) => {
                          if (!v) return;
                          setPositionSection(v as PositionSection);
                        }}
                        className="grid h-8 w-full grid-cols-2 gap-0.5"
                        aria-label="Position type"
                      >
                        <ToggleGroupItem
                          value="mca"
                          size="sm"
                          className="h-full min-w-0 rounded-sm px-1 text-[0.65rem] font-bold uppercase tracking-wide text-muted-foreground shadow-none hover:bg-transparent hover:text-foreground data-[state=on]:bg-background data-[state=on]:text-foreground data-[state=on]:shadow-sm"
                        >
                          MCA ({mcaGroups.length})
                        </ToggleGroupItem>
                        <ToggleGroupItem
                          value="loc_loan"
                          size="sm"
                          className="h-full min-w-0 rounded-sm px-1 text-[0.65rem] font-bold uppercase tracking-wide text-muted-foreground shadow-none hover:bg-transparent hover:text-foreground data-[state=on]:bg-background data-[state=on]:text-foreground data-[state=on]:shadow-sm"
                        >
                          LOC/LOANS ({locOrLoanGroups.length})
                        </ToggleGroupItem>
                      </ToggleGroup>
                    </div>
                    {visibleFunderGroups.length > 0 ? (
                      visibleFunderGroups.map(renderFunderCard)
                    ) : (
                      <div className="px-4 py-6 text-center">
                        <Landmark className="mx-auto mb-2 h-6 w-6 text-muted-foreground" />
                        <p className="mb-1 text-xs font-semibold text-muted-foreground">
                          {positionSection === "mca"
                            ? "No MCA positions detected"
                            : "No LOC / loan positions detected"}
                        </p>
                        <p className="text-[0.65rem] leading-snug text-muted-foreground">
                          {positionSection === "mca"
                            ? "The analysis did not identify any existing MCA withdrawals or deposits in the bank statements."
                            : "The analysis did not identify any lines of credit or loans in the bank statements."}
                        </p>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="px-4 py-6 text-center">
                    <Landmark className="mx-auto mb-2 h-6 w-6 text-muted-foreground" />
                    <p className="mb-1 text-xs font-semibold text-muted-foreground">
                      No MCA positions detected
                    </p>
                    <p className="text-[0.65rem] leading-snug text-muted-foreground">
                      The analysis did not identify any existing MCA withdrawals or deposits in the
                      bank statements.
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* ── Filter & Transactions ── */}
            <div className="flex min-h-0 min-w-0 flex-col overflow-hidden rounded-md border border-border bg-card p-3">
              {calendarTxns.length > 0 || transactions.length > 0 ? (
                <div className="flex min-h-0 flex-col overflow-hidden">
                  <TransactionFiltersComponent
                    filters={listFilters}
                    onFiltersChange={changeListFilters}
                    subCategories={listSubCategories}
                    accountNames={listAccountNames}
                    accountNumbers={listAccountNumbers}
                    tags={listTags}
                  />

                  <div className="h-2" />

                  {keyFilter && (
                    <Badge variant="secondary" className="mb-2 w-fit gap-1 pr-0.5">
                      <span>
                        {keyFilter.length} linked {pluralize(keyFilter.length, "transaction")}
                      </span>
                      <button
                        type="button"
                        className="ml-1 rounded-full p-0.5 hover:bg-muted"
                        onClick={() => {
                          endLink();
                          setKeyFilter(null);
                        }}
                        aria-label="Clear filter"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  )}

                  {selectedFilter && (
                    <Badge variant="secondary" className="mb-2 w-fit gap-1 pr-0.5">
                      <span className="max-w-[280px] truncate">
                        {selectedFilter.type === "funder"
                          ? selectedFilter.name
                          : selectedFilter.type === "withdrawal"
                            ? `${selectedFilter.name} · withdrawal $${selectedFilter.amount}`
                            : `${selectedFilter.name} · deposit $${selectedFilter.amount} on ${selectedFilter.date}`}
                      </span>
                      <button
                        type="button"
                        className="ml-1 rounded-full p-0.5 hover:bg-muted"
                        onClick={() => {
                          endLink();
                          setSelectedFilter(null);
                        }}
                        aria-label="Clear filter"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  )}

                  <div className="min-w-0">
                    <BaseTransactionTable
                      columns={listColumnsFull}
                      items={filteredBorrowerTxns}
                      renderRow={renderBorrowerTxnRow}
                      emptyMessage="No transactions found"
                      defaultRowsPerPage={TXN_TABLE_VIEWPORT_ROWS}
                      scrollViewportRows={TXN_TABLE_VIEWPORT_ROWS}
                      defaultSortColumnIndex={3}
                      defaultSortDirection="desc"
                      dense
                      reveal={reveal}
                      pageResetKey={linkEnds}
                    />
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-3 rounded-md border border-border bg-muted/40 px-4 py-4">
                  <Info className="h-5 w-5 shrink-0 text-muted-foreground" />
                  <div>
                    <p className="mb-0.5 text-xs font-semibold text-muted-foreground">
                      No transaction data
                    </p>
                    <p className="text-[0.65rem] leading-snug text-muted-foreground">
                      Neither the analysis engine nor the database returned transactions for this
                      borrower. Verify that bank statements have been uploaded and processed.
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <DailyBalance
          data={analysis.daily_balances ?? []}
          dailyPayment={analysis.offer?.daily_payment}
          nsfCount={analysis.total_nsf_count}
          nsfDates={nsfDates}
          offerPayment={balanceOffer?.payment}
          offerFrequency={balanceOffer?.frequency}
          offerTerm={balanceOffer?.term}
        />

        <RevenueMonthsDialog
          open={revenueModalOpen}
          onOpenChange={setRevenueModalOpen}
          months={revenueMonths}
          onPick={(month) => {
            handleRevenueMonthClick(month);
            setRevenueModalOpen(false);
          }}
        />

        <Dialog
          open={avgRevenueModalOpen}
          onOpenChange={(open) => {
            if (!open) setAvgRevenueModalOpen(false);
          }}
        >
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Average Revenue Months</DialogTitle>
              <DialogDescription>
                Select which months to include in the average. Full months are selected by default
                (last three); month-to-date can be added.
              </DialogDescription>
            </DialogHeader>
            <div className="flex max-h-[60vh] flex-col gap-2 overflow-y-auto pt-2">
              {avgRevenueMonths.map((m) => {
                const selected = draftAvgMonthKeys.includes(m.monthKey);
                return (
                  <button
                    key={m.monthKey}
                    type="button"
                    aria-pressed={selected}
                    onClick={() => toggleDraftAvgMonth(m.monthKey)}
                    className={cn(
                      "flex items-center justify-between rounded-md border p-3 text-left transition-colors",
                      selected
                        ? "border-primary/40 bg-primary/5 shadow-sm"
                        : "border-border hover:bg-muted/60"
                    )}
                  >
                    <span className="font-medium">
                      {m.month_abbrev}
                      {!m.isFull ? (
                        <span className="ml-2 text-xs font-normal text-muted-foreground">MTD</span>
                      ) : null}
                    </span>
                    <span className="font-bold text-foreground">{m.amount_display}</span>
                  </button>
                );
              })}
            </div>
            <DialogFooter className="gap-2 sm:justify-between">
              <Button type="button" variant="outline" onClick={() => setAvgRevenueModalOpen(false)}>
                Cancel
              </Button>
              <Button
                type="button"
                data-testid="avg-revenue-confirm"
                disabled={draftAvgMonthKeys.length === 0}
                onClick={confirmAvgRevenueMonths}
                aria-label="Save average revenue months"
              >
                <Check className="h-4 w-4" />
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* ===== DIALOGS ===== */}
        {canActOnAssessment && acceptOpen && offerValues && (
          <AcceptDialog
            open={acceptOpen}
            onClose={() => setAcceptOpen(false)}
            activePositions={activePositionsForDialog}
            ordinalPosition={computedOrdinalPosition}
            offerValues={offerValues}
            onConfirm={handleAcceptConfirm}
            submitting={acceptLoadingDelay}
            existingAssessment={isUpdating ? existingAssessment : null}
            applicationNotes={borrower?.application_notes}
            onboardingNotice={onboardingNotice}
            onboardingNoticeError={onboardingNoticeError}
            showSendContractOption={showSendContractOption}
            confirmReady={
              onboardingDecision !== "hold" && (!showSendContractOption || onboardingNotice != null)
            }
          />
        )}

        {canActOnAssessment && (
          <ContractPreviewModal
            open={contractPreview.preview != null}
            preview={contractPreview.preview}
            confirming={contractPreview.busy}
            completed={contractPreview.completed}
            error={contractPreview.actionError}
            onConfirm={() => void confirmOnboardingPreview()}
            onCancel={() => void discardOnboardingPreview("Contract preview cancelled.")}
            onCompleted={contractPreview.markCompleted}
            onRefresh={() => void contractPreview.refresh()}
            onEditAgain={() => void contractPreview.editAgain()}
          />
        )}

        {canActOnAssessment && (
          <DenyDialog
            open={denyOpen}
            onClose={() => setDenyOpen(false)}
            onConfirm={handleDenyConfirm}
            submitting={denyLoadingDelay}
            existingAssessment={isUpdating ? existingAssessment : null}
            applicationNotes={borrower?.application_notes}
          />
        )}

        {canActOnAssessment && existingAssessment && (
          <ChangeDecisionDialog
            open={changeDecisionOpen}
            onClose={() => setChangeDecisionOpen(false)}
            currentStatus={
              existingAssessment.accepted === true
                ? "accepted"
                : existingAssessment.accepted === false
                  ? "declined"
                  : "pending"
            }
            hasOffer={!!offerValues}
            onSelectAccept={() => {
              setIsUpdating(true);
              setAcceptOpen(true);
            }}
            onSelectDeny={() => {
              setIsUpdating(true);
              setDenyOpen(true);
            }}
          />
        )}

        {canActOnFinalDecision && canEditFinalDecision && (
          <Dialog
            open={changeFinalDecisionOpen}
            onOpenChange={(v) => {
              if (!v) {
                setChangeFinalDecisionOpen(false);
                setFinalDecisionError(null);
              }
            }}
          >
            <DialogContent className={cn("max-w-sm sm:max-w-sm", "[&>button.absolute]:hidden")}>
              <DialogHeader className="space-y-0 text-left">
                <div className="flex items-center justify-between pr-8">
                  <div className="flex flex-wrap items-center gap-2">
                    <DialogTitle className="text-xl font-bold">Change Final Decision</DialogTitle>
                    <Badge variant={isFinalDecisionFunded ? "default" : "destructive"}>
                      {isFinalDecisionFunded ? "Funded" : "Declined"}
                    </Badge>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 shrink-0"
                    onClick={() => {
                      setChangeFinalDecisionOpen(false);
                      setFinalDecisionError(null);
                    }}
                    aria-label="Close"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </DialogHeader>

              {finalDecisionError && (
                <Alert variant="destructive" className="py-2">
                  <AlertDescription className="text-sm">{finalDecisionError}</AlertDescription>
                </Alert>
              )}

              <div className="flex gap-3 pt-2">
                <Button
                  type="button"
                  className="h-auto w-full flex-1 flex-col gap-1 bg-green-600 py-6 hover:bg-green-600/90"
                  onClick={() => void handleSetFinalDecision("funded")}
                  disabled={finalDecisionUpdating}
                >
                  <Check className="h-5 w-5" />
                  Fund
                </Button>
                <Button
                  type="button"
                  variant="destructive"
                  className="h-auto w-full flex-1 flex-col gap-1 py-6"
                  onClick={() => void handleSetFinalDecision("declined")}
                  disabled={finalDecisionUpdating}
                >
                  <X className="h-5 w-5" />
                  Decline
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>
    </TooltipProvider>
  );
};

export default FunderAnalysisDashboard;
